// Einfache Formatierung (fett, kursiv, unterstrichen, Aufzaehlung, Einzug) fuer die
// Notizfelder von Anrufen, Aufgaben, Terminen und Notizen.
//
// ponytail: natives `contenteditable` + `execCommand` statt einer Editor-Bibliothek —
// dasselbe Muster wie im Schreibfenster (EmailComposeModal) und im Signatur-Editor,
// und die vier geforderten Befehle kann der Browser von sich aus. Gespeichert wird
// HTML; der Altbestand ist Klartext und wird beim Anzeigen escaped, nicht migriert.
// Ceiling: keine Ueberschriften, Links oder Bilder — dafuer stuende der Weg ueber
// dieselbe Werkzeugleiste offen wie im Schreibfenster.
import React, { useEffect, useRef, useState } from "react";
import { Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import { htmlToPlain, plainToHtml, sanitizeHtml } from "./emailHtml";
import { execEditorCommand, indentOnTab } from "./EmailFormatToolbar";
import HiddenValue from "./HiddenValue";
import { useHideData } from "../hooks/useHideData";

// Ein gespeicherter Wert ist HTML, sobald er ein Tag oder eine Entity traegt — sonst stammt er aus der
// Zeit vor dem Editor (oder aus der HubSpot-Migration) und ist Klartext.
export const isHtmlNote = (value) =>
  /<\/?[a-z][a-z0-9]*(\s[^>]*)?>|&(?:[a-z\d]+|#\d+|#x[a-f\d]+);/i.test(String(value ?? ""));

/** Notizwert zu anzeigbarem HTML — Klartext wird escaped, HTML gefiltert. */
export const noteHtml = (value) => {
  const normalized = String(value ?? "").replace(/&(?:amp;)+nbsp;/gi, "&nbsp;");
  return isHtmlNote(normalized) ? sanitizeHtml(normalized) : plainToHtml(normalized);
};

/** Notizwert als Klartext — fuer einzeilige Vorschauen und `title`. */
export const notePlain = (value) => {
  const normalized = String(value ?? "").replace(/&(?:amp;)+nbsp;/gi, "&nbsp;");
  return isHtmlNote(normalized) ? htmlToPlain(normalized).trim() : normalized;
};

/** Leer heisst leer: `<p><br></p>` aus einem geleerten Feld ist keine Notiz. */
export const noteIsEmpty = (value) => !notePlain(value).trim();

/** Was gespeichert wird: ein geleertes Feld ist "", kein uebrig gebliebenes `<br>`. */
export const noteValue = (value) => (noteIsEmpty(value) ? "" : String(value ?? ""));

/** Klartext an eine Notiz anhaengen, die HTML sein kann. */
export const appendNote = (existing, text) => {
  if (!text) return existing || "";
  if (!existing) return text;
  return isHtmlNote(existing)
    ? `${existing}<br><br>${plainToHtml(text)}`
    : `${existing}\n\n${text}`;
};

/** Notiztext anzeigen.
 *
 * Privacy Mode: a note is free text a user typed — it routinely quotes prices, customer names
 * and agreed terms — so it belongs to the `comments` class. Masking happens HERE rather than at
 * the five call sites in ActivityFeed, because this is the only place that knows the value is a
 * note, and a call site added tomorrow is covered without anyone remembering.
 *
 * The rendered value is HTML (`dangerouslySetInnerHTML`), so <HiddenValue> cannot wrap it: it
 * masks text, and the markup would show through. The plain text is masked instead and rendered
 * as text — formatting is lost while masked, which is the right trade for a value nobody is
 * meant to read. `useHideData()` makes this follow the toggle.
 */
export const NoteText = ({ value, as: Tag = "div", ...rest }) => {
  const { isMasked } = useHideData();
  if (isMasked("comments")) {
    return (
      <Tag {...rest}>
        <HiddenValue dataClass="comments">{notePlain(value)}</HiddenValue>
      </Tag>
    );
  }
  return <Tag {...rest} dangerouslySetInnerHTML={{ __html: noteHtml(value) }} />;
};

const COMMANDS = [
  ["bold", "format-bold", "Bold"],
  ["italic", "format-italic", "Italic"],
  ["underline", "format-underline", "Underline"],
  ["insertUnorderedList", "format-list-bulleted", "Bullet list"],
];

// Kein Zustand zum Anzeigen — Knopf ohne `aria-pressed`.
const INDENT_COMMANDS = [
  ["outdent", "format-indent-decrease", "Decrease indent", "Shift+Tab"],
  ["indent", "format-indent-increase", "Increase indent", "Tab"],
];

/**
 * Ersatz fuer `<Form.Control as="textarea">` in Notizfeldern.
 *
 * @param value        HTML oder Klartext
 * @param onChange     bekommt HTML
 * @param rows         Hoehe wie bei einem Textfeld
 */
const RichNoteField = ({ value, onChange, rows = 3, placeholder, disabled }) => {
  const { t } = useTranslation(["components", "common"]);
  const ref = useRef(null);
  const [format, setFormat] = useState({});

  // Der Wert kommt von aussen nur ins Feld, solange dort nicht getippt wird — sonst
  // setzt jeder Tastendruck `innerHTML` neu und der Cursor springt ans Ende.
  useEffect(() => {
    const field = ref.current;
    if (!field || field === document.activeElement) return;
    const html = noteHtml(value);
    if (field.innerHTML !== html) field.innerHTML = html;
  }, [value]);

  // Am Dokument, nicht am Feld: die Auswahl aendert sich auch per Tastatur.
  useEffect(() => {
    const read = () => {
      const selection = window.getSelection();
      if (!selection?.anchorNode || !ref.current?.contains(selection.anchorNode)) return;
      setFormat(
        Object.fromEntries(
          COMMANDS.map(([command]) => [command, document.queryCommandState(command)])
        )
      );
    };
    document.addEventListener("selectionchange", read);
    return () => document.removeEventListener("selectionchange", read);
  }, []);

  const exec = (command, argument) => {
    const field = ref.current;
    if (!field) return;
    // `execCommand` braucht eine Auswahl IM Feld. Die Knoepfe geben den Fokus nicht her
    // (`onMouseDown`), nach einem Klick von aussen steht der Cursor am Anfang.
    field.focus();
    execEditorCommand(command, argument);
    onChange(field.innerHTML);
  };

  return (
    <div>
      <div className="d-flex gap-1 mb-1">
        {COMMANDS.map(([command, icon, label]) => (
          <Button
            key={command}
            variant="link"
            size="sm"
            disabled={disabled}
            className={`text-body px-1 py-0 rounded${format[command] ? " bg-light" : ""}`}
            title={t(label)}
            aria-label={t(label)}
            aria-pressed={!!format[command]}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => exec(command)}
          >
            <i className={`mdi mdi-${icon}`} />
          </Button>
        ))}
        {INDENT_COMMANDS.map(([command, icon, label, key]) => (
          <Button
            key={command}
            variant="link"
            size="sm"
            disabled={disabled}
            className="text-body px-1 py-0"
            title={`${t(label)} (${key})`}
            aria-label={t(label)}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => exec(command)}
          >
            <i className={`mdi mdi-${icon}`} />
          </Button>
        ))}
      </div>

      <div className="position-relative">
        <div
          ref={ref}
          contentEditable={!disabled}
          suppressContentEditableWarning
          role="textbox"
          aria-multiline="true"
          aria-label={placeholder || t("Note")}
          className="form-control"
          // `.form-control` ist in _formControl.scss eine Pille; die Ausnahme fuer
          // mehrzeilige Felder haengt am Selektor `textarea.form-control` und greift
          // hier nicht. Derselbe Wert, damit das Feld aussieht wie das Textfeld vorher.
          style={{
            borderRadius: "1rem",
            minHeight: `${rows * 1.5 + 0.75}rem`,
            overflowY: "auto",
          }}
          onInput={() => onChange(ref.current.innerHTML)}
          onKeyDown={(event) => indentOnTab(event, onChange)}
          onBlur={() => onChange(ref.current.innerHTML)}
          // Eingefuegtes HTML wird sofort gefiltert, nicht erst beim Speichern — bis
          // dahin haengt es im DOM dieser Seite. Wie im Schreibfenster.
          onPaste={(event) => {
            event.preventDefault();
            const clip = event.clipboardData;
            const pasted = clip.getData("text/html");
            exec(
              "insertHTML",
              pasted ? sanitizeHtml(pasted) : plainToHtml(clip.getData("text/plain"))
            );
          }}
        />
        {/* Ein contenteditable kennt kein `placeholder`. */}
        {placeholder && noteIsEmpty(value) && (
          <span
            className="text-muted position-absolute"
            style={{ top: "0.45rem", left: "0.9rem", pointerEvents: "none" }}
          >
            {placeholder}
          </span>
        )}
      </div>
    </div>
  );
};

export default RichNoteField;
